import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Zap, ToggleLeft, ToggleRight, 
  Settings, Key, TestTube, Save, X, Image as ImageIcon,
  Sparkles, Palette, Type, Building2, AlertCircle
} from 'lucide-react';
import api from '../services/api';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const providerOptions = {
  leonardo: {
    label: 'Leonardo.ai',
    description: 'Excelente para marketing, 150 imagens/dia grátis',
    icon: '🎨',
    defaultUrl: 'https://cloud.leonardo.ai/api/rest/v1',
    color: 'from-purple-500 to-pink-500'
  },
  openai: {
    label: 'OpenAI DALL-E',
    description: 'Alta qualidade, ~$0.04 por imagem',
    icon: '🤖',
    defaultUrl: 'https://api.openai.com/v1',
    color: 'from-green-500 to-blue-500'
  },
  stability: {
    label: 'Stability AI',
    description: 'Mais barato, ~$0.01 por imagem',
    icon: '⚡',
    defaultUrl: 'https://api.stability.ai/v1',
    color: 'from-orange-500 to-red-500'
  }
};

function Automations() {
  const [automations, setAutomations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', type: 'keyword',
    trigger_config: { keywords: [] },
    actions: { action: 'send_message', message: '' }
  });

  // Estados para configuração de IA
  const [showAiConfigModal, setShowAiConfigModal] = useState(false);
  const [aiConfig, setAiConfig] = useState(null);
  const [aiForm, setAiForm] = useState({
    provider: 'leonardo',
    api_key: '',
    api_url: '',
    settings: {},
    monthly_limit: 50,
    is_active: true
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { 
    loadData();
    loadAiConfig();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/api/automations');
      setAutomations(res.data);
    } catch (err) { 
      console.error(err); 
    }
  };

  const loadAiConfig = async () => {
    try {
      const res = await api.get('/api/ai/config');
      setAiConfig(res.data);
      if (res.data.configured) {
        setAiForm({
          provider: res.data.provider,
          api_key: '', // Não preencher por segurança
          api_url: res.data.api_url || providerOptions[res.data.provider]?.defaultUrl || '',
          settings: res.data.settings || {},
          monthly_limit: res.data.monthly_limit || 50,
          is_active: res.data.is_active
        });
      }
    } catch (err) {
      console.error('Erro ao carregar config de IA:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/api/automations/${editing.id}`, form);
      } else {
        await api.post('/api/automations', form);
      }
      setShowModal(false);
      setEditing(null);
      setForm({ name: '', type: 'keyword', trigger_config: { keywords: [] }, actions: { action: 'send_message', message: '' } });
      loadData();
    } catch (err) { 
      alert('Erro: ' + (err.response?.data?.error || err.message)); 
    }
  };

  const handleEdit = (automation) => {
    setEditing(automation);
    setForm({
      name: automation.name,
      type: automation.type,
      trigger_config: automation.trigger_config,
      actions: automation.actions
    });
    setShowModal(true);
  };

  const handleToggle = async (automation) => {
    try {
      await api.put(`/api/automations/${automation.id}`, { is_active: !automation.is_active });
      loadData();
    } catch (err) { 
      alert('Erro ao atualizar'); 
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Excluir automação?')) {
      try { 
        await api.delete(`/api/automations/${id}`); 
        loadData(); 
      } catch (err) { 
        alert('Erro ao excluir'); 
      }
    }
  };

  // ============================================
  // CONFIGURAÇÃO DE IA
  // ============================================
  const handleProviderChange = (provider) => {
    setAiForm({
      ...aiForm,
      provider,
      api_url: providerOptions[provider]?.defaultUrl || ''
    });
  };

  const handleTestConnection = async () => {
    if (!aiForm.api_key) {
      alert('Digite a API Key primeiro');
      return;
    }

    setTesting(true);
    try {
      const res = await api.post('/api/ai/test', aiForm);
      if (res.data.success) {
        alert('✅ Conexão testada com sucesso!');
      } else {
        alert('❌ Falha no teste: ' + res.data.error);
      }
    } catch (err) {
      alert('❌ Erro ao testar: ' + (err.response?.data?.error || err.message));
    } finally {
      setTesting(false);
    }
  };

  const handleSaveAiConfig = async () => {
    if (!aiForm.api_key && !aiConfig?.configured) {
      alert('Digite a API Key');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/api/ai/config', aiForm);
      if (res.data.success) {
        alert('✅ Configuração salva com sucesso!');
        setShowAiConfigModal(false);
        loadAiConfig();
      }
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAiConfig = async () => {
    if (!window.confirm('Remover configuração de IA?')) return;

    try {
      await api.delete('/api/ai/config');
      alert('✅ Configuração removida');
      setAiConfig(null);
      setAiForm({
        provider: 'leonardo',
        api_key: '',
        api_url: '',
        settings: {},
        monthly_limit: 50,
        is_active: true
      });
      setShowAiConfigModal(false);
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Automações</h1>
          <p className="text-gray-500">Configure respostas automáticas e IA para geração de imagens</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowAiConfigModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-200">
            <Sparkles size={18} />
            Configurar IA
          </button>
          <button 
            onClick={() => { 
              setEditing(null); 
              setForm({ name: '', type: 'keyword', trigger_config: { keywords: [] }, actions: { action: 'send_message', message: '' } }); 
              setShowModal(true); 
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 shadow-lg shadow-green-100">
            <Plus size={18} /> Nova Automação
          </button>
        </div>
      </div>

      {/* ⭐ CARD DE STATUS DA IA */}
      {aiConfig?.configured && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`p-3 bg-gradient-to-br ${providerOptions[aiConfig.provider]?.color} text-white rounded-xl`}>
                <Sparkles size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">
                  {providerOptions[aiConfig.provider]?.label} Configurado
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  API Key: <code className="bg-white px-2 py-0.5 rounded text-xs">{aiConfig.api_key_masked}</code>
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <div className="text-sm">
                    <span className="text-gray-500">Uso mensal:</span>{' '}
                    <span className="font-bold text-gray-900">{aiConfig.used_this_month}/{aiConfig.monthly_limit}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Status:</span>{' '}
                    <span className={`font-bold ${aiConfig.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {aiConfig.is_active ? '✓ Ativo' : '✗ Inativo'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowAiConfigModal(true)}
              className="px-4 py-2 bg-white border border-purple-200 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50"
            >
              Editar
            </button>
          </div>
        </div>
      )}

      {/* Lista de Automações */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {automations.map(automation => (
          <div key={automation.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                <Zap size={24} />
              </div>
              <button onClick={() => handleToggle(automation)}
                className={`p-1.5 rounded-lg ${automation.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}>
                {automation.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
              </button>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">{automation.name}</h3>
            <p className="text-sm text-gray-500 mb-4">
              Tipo: <span className="font-medium text-gray-700">{automation.type}</span>
            </p>
            <div className="text-xs text-gray-500 mb-4">
              <p className="font-medium text-gray-700 mb-1">Gatilho:</p>
              <p>{JSON.stringify(automation.trigger_config)}</p>
            </div>
            <div className="text-xs text-gray-500 mb-4">
              <p className="font-medium text-gray-700 mb-1">Ação:</p>
              <p>{JSON.stringify(automation.actions)}</p>
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-50">
              <button onClick={() => handleEdit(automation)} className="flex-1 p-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium">
                <Edit2 size={16} className="inline mr-1" /> Editar
              </button>
              <button onClick={() => handleDelete(automation.id)} className="flex-1 p-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">
                <Trash2 size={16} className="inline mr-1" /> Excluir
              </button>
            </div>
          </div>
        ))}
        {automations.length === 0 && <p className="col-span-full text-center text-gray-400 py-12">Nenhuma automação configurada.</p>}
      </div>

      {/* ============================================ */}
      {/* MODAL DE AUTOMAÇÃO */}
      {/* ============================================ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
            <h2 className="text-lg font-bold mb-4">{editing ? 'Editar Automação' : 'Nova Automação'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" placeholder="Nome da automação *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                <option value="keyword">Palavra-chave</option>
                <option value="schedule">Agendamento</option>
                <option value="event">Evento</option>
              </select>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Palavras-chave (separadas por vírgula)</label>
                <input type="text" placeholder="ola, oi, bom dia"
                  value={(form.trigger_config.keywords || []).join(', ')}
                  onChange={e => setForm({...form, trigger_config: { ...form.trigger_config, keywords: e.target.value.split(',').map(k => k.trim()) }})}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de resposta</label>
                <textarea placeholder="Olá! Como posso ajudar?"
                  value={form.actions.message || ''}
                  onChange={e => setForm({...form, actions: { ...form.actions, message: e.target.value }})}
                  rows={3}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none" />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL DE CONFIGURAÇÃO DE IA */}
      {/* ============================================ */}
      {showAiConfigModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles size={24} />
                  Configurar IA para Geração de Imagens
                </h2>
                <p className="text-sm opacity-90 mt-1">
                  Conecte sua conta de IA preferida
                </p>
              </div>
              <button
                onClick={() => setShowAiConfigModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Seleção de Provedor */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Selecione o Provedor de IA
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(providerOptions).map(([key, provider]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleProviderChange(key)}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        aiForm.provider === key
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-3xl mb-2">{provider.icon}</div>
                      <p className="font-bold text-gray-900">{provider.label}</p>
                      <p className="text-xs text-gray-500 mt-1">{provider.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Key size={14} className="inline mr-1" />
                  API Key *
                </label>
                <input
                  type="password"
                  value={aiForm.api_key}
                  onChange={e => setAiForm({...aiForm, api_key: e.target.value})}
                  placeholder={aiConfig?.configured ? 'Deixe vazio para manter a atual' : 'Cole sua API Key aqui'}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {aiConfig?.configured && aiConfig.api_key_masked && (
                    <span>Atual: <code className="bg-gray-100 px-1 rounded">{aiConfig.api_key_masked}</code></span>
                  )}
                </p>
              </div>

              {/* API URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API URL (opcional)
                </label>
                <input
                  type="text"
                  value={aiForm.api_url}
                  onChange={e => setAiForm({...aiForm, api_url: e.target.value})}
                  placeholder={providerOptions[aiForm.provider]?.defaultUrl}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deixe vazio para usar a URL padrão
                </p>
              </div>

              {/* Limite Mensal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Limite Mensal de Imagens
                </label>
                <input
                  type="number"
                  value={aiForm.monthly_limit}
                  onChange={e => setAiForm({...aiForm, monthly_limit: parseInt(e.target.value) || 50})}
                  min="1"
                  max="1000"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Máximo de imagens que podem ser geradas por mês
                </p>
              </div>

              {/* Ativo/Inativo */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiForm.is_active}
                    onChange={e => setAiForm({...aiForm, is_active: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">IA Ativa</span>
                </label>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={handleTestConnection}
                  disabled={testing || !aiForm.api_key}
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TestTube size={16} />
                  {testing ? 'Testando...' : 'Testar Conexão'}
                </button>
                <button
                  onClick={handleSaveAiConfig}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-sm font-bold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
                >
                  <Save size={16} />
                  {saving ? 'Salvando...' : 'Salvar Configuração'}
                </button>
                {aiConfig?.configured && (
                  <button
                    onClick={handleDeleteAiConfig}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700"
                  >
                    <Trash2 size={16} />
                    Remover
                  </button>
                )}
              </div>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-900 font-semibold mb-2">
                  💡 Como obter sua API Key:
                </p>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li><strong>Leonardo.ai:</strong> Acesse <a href="https://leonardo.ai" target="_blank" rel="noopener noreferrer" className="underline">leonardo.ai</a> → Configurações → API Keys</li>
                  <li><strong>OpenAI:</strong> Acesse <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer" className="underline">platform.openai.com</a> → API Keys</li>
                  <li><strong>Stability AI:</strong> Acesse <a href="https://platform.stability.ai" target="_blank" rel="noopener noreferrer" className="underline">platform.stability.ai</a> → API Keys</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Automations;